import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import crossIcon from '../../../../../_metronic/assets/images/svg/data-room/cross-black-icon.svg'
import linkFileIcon from '../../../../../_metronic/assets/images/svg/data-room/create-link-file-icon.svg'
import calendarIcon from '../../../../../_metronic/assets/images/svg/data-room/calendar-solid-icon.svg'
import { Formik, ErrorMessage, Form } from 'formik'
import * as Yup from 'yup'
import { useAuth } from '../../../auth'
import { createShareLink, createShareLinkFile, getAccounts } from '../../core/_requests'
import { RestrictAccessDrawer } from './RestrictAccessDrawer'
import { toast } from 'react-toastify'
import { useMoveToFolderData } from '../../core/FileToFolderAuth'
import { LinkCreatedModal } from './LinkCreatedModal'
import useShareLinks from '../../../../hooks/getAllShareLinkApi'
import useAllDataRooms from '../../../../hooks/getAllDataRoomsById'
import { CustomButton } from '../../../widgets/components/UI/CustomButton'
import TextInput from '../../../widgets/components/Input/TextInput'
import CreatableSelect from 'react-select/creatable'
import TagManager from 'react-gtm-module'
import { GTMEvent } from '../../../../hooks/gtmHelpers'
import { ToolTipUIForModal } from '../../../widgets/components/UI/ToolTipUIForModal'
import { useThemeMode } from '../../../../../_metronic/partials'

export const CreateLinkDrawer = ({ dataRoomId, contentId }: any) => {
  const { formatMessage } = useIntl()
  const { mode } = useThemeMode()
  const { companyId, currentUser } = useAuth()
  const [loading, setLoading] = useState(false)
  const [linkId, setLinkId] = useState(null)
  const [passcodeEnabled, setPasscodeEnabled] = useState(false)
  const [dateEnabled, setDateEnabled] = useState(false)
  const [allowDownload, setAllowDownload] = useState(false)
  const [requireEmail, setRequireEmail] = useState(false)
  const [, setImgName] = useState<string | undefined>()
  const [accessControlEnabled, setAccessControlEnabled] = useState(false)
  const [restrictAccessEnabled, setRestrictAccessEnabled] = useState(false)
  const [editPermissionsEnabled, setEditPermissionsEnabled] = useState(false)
  const { setLinkCreatedModal, createLinkDrawer } = useMoveToFolderData()
  const [currentDate, setCurrentDate] = useState('')
  const [aclList, setAclList] = useState<string[]>([])
  const [emailAuthentication, setEmailAuthentication] = useState(false)
  const [account, setAccount] = useState('')
  const [accountError, setAccountError] = useState(false)
  const [touched, setTouched] = useState(false)
  const [accountData, setAccountData] = useState([])

  useEffect(() => {
    const today = new Date()
    const formattedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(
      2,
      '0'
    )}-${String(today.getDate()).padStart(2, '0')}`
    setCurrentDate(formattedDate)
  }, [])

  const handleAccessControlChange = (event: any) => {
    const isChecked = event.target.checked
    setAccessControlEnabled(isChecked)
    if (!isChecked) {
      setRestrictAccessEnabled(isChecked)
      setEmailAuthentication(isChecked)
    }
  }

  const handleEmailAuthentication = (event: any, formik: any) => {
    const isChecked = event.target.checked
    setEmailAuthentication(isChecked)
    formik.setFieldValue('emailAuthentication', isChecked)
  }

  const handleRestrictAccessChange = (event: any, formik: any) => {
    const isChecked = event.target.checked
    setRestrictAccessEnabled(isChecked)
    setEditPermissionsEnabled(isChecked)
    setEmailAuthentication(isChecked)
    formik.setFieldValue('aclEnabled', isChecked)
    formik.setFieldValue('emailAuthentication', isChecked)
    formik.setFieldValue('aclList', [])
    setAclList([])
  }

  const createShareLinkSchema = (isPasscodeEnabled: boolean) => {
    return Yup.object().shape({
      passcode: isPasscodeEnabled
        ? Yup.string()
          .required(formatMessage({ id: '*Passcode is required' }))
          .min(6)
        : Yup.string().min(6),
    })
  }

  enum ACLTYPE {
    ALLOW = 'ALLOW',
    BLOCK = 'BLOCK',
  }

  const { setRestrictAcessDrawer, setCreateLinkDrawer } = useMoveToFolderData()
  const { getAllShareLinksByDataRoomId } = useShareLinks(dataRoomId, contentId)
  const { getAllDataRoomsById } = useAllDataRooms()

  const handleEditPermissionsClick = () => {
    setRestrictAcessDrawer(true)
  }

  const initialValues = {
    enabled: true,
    passcode: '',
    expiryDate: '',
    allowDownload: false,
    requireEmail: false,
    watermark: '',
    emailAuthentication: false,
    aclEnabled: false,
    aclType: ACLTYPE.ALLOW,
    aclList: [],
    expiryonlyDate: '',
    expiryTime: '',
    companyId: companyId,
  }

  const handleDrawerClose = (formik: any) => {
    setCreateLinkDrawer(false)
    formik.resetForm()
    setPasscodeEnabled(false)
    setDateEnabled(false)
    setAllowDownload(false)
    setRequireEmail(false)
    setImgName('')
    setAccessControlEnabled(false)
    setEditPermissionsEnabled(false)
    setRestrictAccessEnabled(false)
    setEmailAuthentication(false)
    setAclList([])
  }

  const updateExpiryDate = (expiryonlyDate: any, expiryTime: any, setFieldValue: any) => {
    if (expiryonlyDate && expiryTime) {
      const combinedDateTime = new Date(`${expiryonlyDate} ${expiryTime}`).toISOString()
      setFieldValue('expiryDate', combinedDateTime)
    } else {
      setFieldValue('expiryDate', '')
    }
  }

  const handleSubmit = async (values: any, { resetForm }: any) => {
    setLoading(true)
    if (!account) {
      setAccountError(true)
      setLoading(false)
      return
    }
    setAccountError(false)
    try {
      if (account && dataRoomId) {
        const {
          data: { success, errors, data },
        } = await createShareLink(+dataRoomId, { ...values, account })
        if (success) {
          setLoading(false)
          resetForm()
          setPasscodeEnabled(false)
          setDateEnabled(false)
          setAllowDownload(false)
          setRequireEmail(false)
          setImgName('')
          setAccessControlEnabled(false)
          setEditPermissionsEnabled(false)
          setRestrictAccessEnabled(false)
          setEmailAuthentication(false)
          setAclList([])
          setLinkId(data.id)
          toast.success(formatMessage({ id: 'Share Link Created successfully' }))
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.CreateShareLinkSuccessful,
              action: 'normal',
              metadata: {
                enabled: values.enabled,
                expiry_date: values.expiryDate,
                allow_download: values.allowDownload,
                require_email: values.requireEmail,
                watermark: values.watermark,
                email_authentication: values.emailAuthentication,
                acl_enabled: values.aclEnabled,
                acl_type: values.aclType,
                expiryonly_date: values.expiryonlyDate,
                expiry_time: values.expiryTime,
                dataroom_id: dataRoomId,
                company_id: companyId,
                user_id: currentUser?.userId,
                email: currentUser?.email,
              },
            },
          })
        } else {
          setImgName('')
          errors.forEach((error: string) => {
            toast.error(formatMessage({ id: error }))
          })
          TagManager.dataLayer({
            dataLayer: {
              event: GTMEvent.CreateShareLinkFailed,
              action: 'normal',
              metadata: {
                enabled: values.enabled,
                expiry_date: values.expiryDate,
                allow_download: values.allowDownload,
                require_email: values.requireEmail,
                watermark: values.watermark,
                email_authentication: values.emailAuthentication,
                acl_enabled: values.aclEnabled,
                acl_type: values.aclType,
                expiryonly_date: values.expiryonlyDate,
                expiry_time: values.expiryTime,
                dataroom_id: dataRoomId,
                company_id: companyId,
                user_id: currentUser?.userId,
                email: currentUser?.email,
                error: errors[0],
              },
            },
          })
        }
      } else {
        if (account && contentId) {
          const {
            data: { success, errors, data },
          } = await createShareLinkFile(contentId, { ...values, account })
          if (success) {
            setLoading(false)
            resetForm()
            setPasscodeEnabled(false)
            setDateEnabled(false)
            setAllowDownload(false)
            setRequireEmail(false)
            setImgName('')
            setAccessControlEnabled(false)
            setEditPermissionsEnabled(false)
            setRestrictAccessEnabled(false)
            setEmailAuthentication(false)
            setAclList([])
            toast.success(formatMessage({ id: 'Share Link Created successfully' }))
            TagManager.dataLayer({
              dataLayer: {
                event: GTMEvent.CreateShareLinkFileSuccessful,
                action: 'normal',
                metadata: {
                  enabled: values.enabled,
                  expiry_date: values.expiryDate,
                  allow_download: values.allowDownload,
                  require_email: values.requireEmail,
                  watermark: values.watermark,
                  email_authentication: values.emailAuthentication,
                  acl_enabled: values.aclEnabled,
                  acl_type: values.aclType,
                  expiryonly_date: values.expiryonlyDate,
                  expiry_time: values.expiryTime,
                  content_id: contentId,
                  company_id: companyId,
                  user_id: currentUser?.userId,
                  email: currentUser?.email,
                },
              },
            })
            setLinkId(data.id)
            setLinkCreatedModal(true)
          } else {
            setImgName('')
            errors.forEach((error: string) => {
              toast.error(formatMessage({ id: error }))
            })
            TagManager.dataLayer({
              dataLayer: {
                event: GTMEvent.CreateShareLinkFileFailed,
                action: 'normal',
                metadata: {
                  enabled: values.enabled,
                  expiry_date: values.expiryDate,
                  allow_download: values.allowDownload,
                  require_email: values.requireEmail,
                  watermark: values.watermark,
                  email_authentication: values.emailAuthentication,
                  acl_enabled: values.aclEnabled,
                  acl_type: values.aclType,
                  expiryonly_date: values.expiryonlyDate,
                  expiry_time: values.expiryTime,
                  content_id: contentId,
                  company_id: companyId,
                  user_id: currentUser?.userId,
                  email: currentUser?.email,
                  error: errors[0],
                },
              },
            })
          }
        }
      }
    } catch (err) {
      console.log(err)
    } finally {
      setLoading(false)
    }
  }

  const onClose = () => {
    setLinkCreatedModal(false)
    setCreateLinkDrawer(false)
    getAllShareLinksByDataRoomId()
    getAllDataRoomsById()
  }
  useEffect(() => {
    if (!account && touched) {
      setAccountError(true)
    } else {
      setAccountError(false)
    }
  }, [account, touched])

  const handleOnBlur = () => {
    if (!touched) {
      setTouched(true)
    }
  }

  const getAllAccounts = async () => {
    try {
      if (companyId) {
        const {
          data: { data, success },
        } = await getAccounts(companyId)
        if (success) {
          setAccountData(
            data?.map((account: { id: number; name: string }) => {
              return {
                label: account.name,
                value: account.name,
                isFixed: true,
              }
            })
          )
        }
      }
    } catch (e) {
      console.log(e)
    }
  }
  useEffect(() => {
    getAllAccounts()
  }, [companyId]) // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={createShareLinkSchema(passcodeEnabled)}
        onSubmit={(values, formikHelpers) => handleSubmit(values, formikHelpers)}
      >
        {(formik) => {
          return (
            <div
              id='kt_create_link'
              className={
                createLinkDrawer
                  ? 'bg-body drawer drawer-end drawer-on'
                  : 'bg-body drawer drawer-end'
              }
              data-kt-drawer='true'
              data-kt-drawer-name='activities'
              data-kt-drawer-activate='true'
              data-kt-drawer-overlay='true'
              data-kt-drawer-width="{default:'320px', 'md': '660px' ,'lg': '847px'}"
              data-kt-drawer-direction='end'
              data-kt-drawer-toggle='#kt_create_link_toggle'
              data-kt-drawer-close='#kt_create_link_close'
            >
              <div className='create_link-drawer d-flex flex-row'>
                <div className=' create_link-drawer-wrapper card shadow-none rounded-0'>
                  <div
                    className='drawer-header d-flex flex-row align-items-center justify-content-between flex-wrap  '
                    id='kt_create_link_header'
                  >
                    <div className='d-flex flex-row align-items-center   w-100'>
                      <button
                        type='button'
                        className='btn p-0 btn-active-light-primary me-4 '
                        onClick={() => handleDrawerClose(formik)}
                      >
                        <img src={crossIcon} alt=''></img>
                      </button>

                      <div className='d-flex flex-row align-items-center  justify-content-between flex-grow-1 '>
                        <h3 className='drawer-head-title  mb-0 me-4 text-dark'>
                          {dataRoomId
                            ? formatMessage({ id: 'Create Link to this Dataroom' })
                            : formatMessage({ id: 'Create Link to this File' })}
                        </h3>
                        {dataRoomId ? (
                          <ToolTipUIForModal
                            tooltipText={formatMessage({
                              id: 'GLOBAL.TOOLTIP.DATAROOM.CREATE_LINK_DRAWER',
                            })}
                          />
                        ) : (
                          <ToolTipUIForModal
                            tooltipText={formatMessage({
                              id: 'GLOBAL.TOOLTIP.DATAROOM.FILES.CREATE_LINK_DRAWER',
                            })}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  <div
                    className='create_link-drawer-body px-5 pt-4  pt-md-7 px-md-7 pb-md-0 '
                    id='kt_create_link_body'
                  >
                    <div
                      id='kt_create_link_scroll'
                      className='w-100 d-flex flex-column align-items-center mb-5 mt-2 activity-container-n overflow-visible'
                    >
                      <div className='create-link-info-wrapper' style={mode === 'dark' ? { background: 'rgb(40 40 63)' } : {}}>
                        <img src={linkFileIcon} alt=''></img>
                        <p className='text-dark'>{formatMessage({ id: 'Creating links?' })} </p>
                        <span className='text-dark'>
                          {formatMessage({
                            id: 'Company basic information is essential for setting up the dashboard. Professional introduction of the business which aims to inform...',
                          })}
                        </span>
                      </div>
                      <Form onSubmit={formik.handleSubmit} className='w-100'>
                        <div className='mb-6'>
                          <label htmlFor='exampleInputText1' className='form-label'>
                            {formatMessage({ id: 'Account*' })}
                          </label>

                          <CreatableSelect
                            isClearable
                            options={accountData}
                            onChange={(e: any) => {
                              setAccount(e.value)
                            }}
                            placeholder={formatMessage({ id: 'Select or Create Account' })}
                            onBlur={handleOnBlur}
                            styles={{
                              control: (base, state) => ({
                                ...base,
                                background: mode === 'dark' ? "#181c32" : '#fff',
                                color: mode === 'dark' ? '#fff' : 'black',
                                width: '100%',
                                borderRadius: '8px',
                                borderColor: state.isFocused ? 'transparent' : mode === 'dark' ? '#323248' : '#e4e6ef',
                                '&:hover': {
                                  borderColor: mode === 'dark' ? '#474761' : '#e4e6ef',
                                },
                              }),
                              placeholder: (base) => ({
                                ...base,
                                color: '#8898a6',
                              }),
                              input: (base) => ({
                                ...base,
                                color: mode === 'dark' ? '#fff' : 'black',
                              }),
                              menu: (base) => ({
                                ...base,
                                background: mode === "dark" ? '#323248' : '#fff',
                              }),
                              option: (base, state) => ({
                                ...base,
                                color: state.isSelected ? '#fff' : base.color,
                                backgroundColor: 'transparent',
                                '&:hover': {
                                  backgroundColor: mode === 'dark' ? '#8898a6' : '#f0f0f0',
                                },
                              }),
                              singleValue: (base) => ({
                                ...base,
                                color: mode === 'dark' ? '#fff' : 'black', // Change the color of the selected option shown inside the input box
                              }),
                            }}
                          />
                          {accountError && (
                            <div className='text-danger mt-1'>
                              {formatMessage({
                                id: 'Account Name is required',
                              })}
                            </div>
                          )}

                          <div id='textHelp' className='form-text'>
                            {formatMessage({
                              id: 'Accounts can be used to group viewers by their organization or company. All viewer activity, across all documents, will be aggregated at the account level.',
                            })}
                          </div>
                        </div>
                        <div className='mb-6'>
                          <div className='mb-3 form-check d-flex flex-row align-items-center'>
                            <input
                              type='checkbox'
                              name='passcode'
                              className='form-check-input'
                              aria-describedby='passcodeTextHelp'
                              id='Passcode'
                              checked={passcodeEnabled}
                              onChange={() => {
                                setPasscodeEnabled(!passcodeEnabled)
                                formik.setFieldValue('passcode', '')
                              }}
                            />
                            <label className='form-check-label mx-3 ' htmlFor='Passcode'>
                              {formatMessage({ id: 'Passcode' })}
                            </label>
                            <ToolTipUIForModal
                              tooltipText={formatMessage({
                                id: 'GLOBAL.TOOLTIP.DATAROOM.CREATE_LINK_DRAWER.PASSWORD',
                              })}
                            />
                          </div>
                          <input
                            type='text'
                            name='passcode'
                            placeholder={formatMessage({ id: 'Passcode (min. 6 characters)' })}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.passcode}
                            className='form-control'
                            id='exampleInputPassword1'
                            disabled={!passcodeEnabled}
                          />
                          <ErrorMessage
                            name='passcode'
                            component='div'
                            className='error-message text-danger'
                          />
                          <div id='passcodeTextHelp' className='form-text'>
                            {formatMessage({
                              id: 'Viewers must enter above passcode(min. 6 characters) to open your dataroom.',
                            })}
                          </div>
                        </div>
                        <div className='mb-6'>
                          <div className='mb-3 form-check d-flex flex-row align-items-center'>
                            <input
                              type='checkbox'
                              className='form-check-input'
                              id='Expires'
                              onChange={() => {
                                setDateEnabled(!dateEnabled)
                                formik.setFieldValue('expiryonlyDate', '')
                                formik.setFieldValue('expiryTime', '')
                              }}
                              checked={dateEnabled}
                            />
                            <label className='form-check-label mx-3' htmlFor='Expires'>
                              {formatMessage({ id: 'Expires' })}
                            </label>
                            <ToolTipUIForModal
                              tooltipText={formatMessage({
                                id: 'GLOBAL.TOOLTIP.DATAROOM.CREATE_LINK_DRAWER.EXPIRY_DATE',
                              })}
                            />
                          </div>

                          <div className='d-flex flex-column flex-md-row align-items-center Expires-input-grid'>
                            <div className='flex-grow-1 w-100 position-relative'>
                              <input
                                type='date'
                                className='form-control flex-grow-1'
                                name='expiryonlyDate'
                                id='Expires-date'
                                placeholder={formatMessage({ id: 'Select date' })}
                                onChange={(event) => {
                                  formik.handleChange(event)
                                  const expiryonlyDate = event.target.value
                                  const expiryTime = formik.values.expiryTime
                                  updateExpiryDate(expiryonlyDate, expiryTime, formik.setFieldValue)
                                }}
                                onBlur={formik.handleBlur}
                                value={formik.values.expiryonlyDate}
                                disabled={!dateEnabled}
                                min={currentDate}
                              />
                              <img src={calendarIcon} className='position-absolute' alt='' />
                            </div>
                            <div className='flex-grow-1 w-100 position-relative '>
                              <TextInput
                                fieldType={'time'}
                                fieldName={'expiryTime'}
                                formik={formik}
                                placeholder=''
                                isTooltipNotRequired={true}
                                isWidthNotRequired={true}
                                isDisabled={!dateEnabled}
                                isMargin={'mb-0'}
                                width={100}
                              />
                            </div>
                          </div>

                          <div id='' className='form-text mt-3'>
                            {formatMessage({ id: 'Viewers can download your file.' })}
                          </div>
                        </div>

                        <div className='mb-5 form-check d-flex flex-row '>
                          <input
                            type='checkbox'
                            className='form-check-input'
                            id='AllowDownloading'
                            name='allowDownload'
                            onChange={(event) => {
                              const isChecked = event.target.checked
                              setAllowDownload(isChecked)
                              formik.setFieldValue('allowDownload', isChecked)
                            }}
                            checked={allowDownload}
                          />
                          <label className='form-check-label mx-3 mt-n1' htmlFor='AllowDownloading'>
                            {formatMessage({ id: 'Allow downloading ' })}
                            <br></br>{' '}
                            <span className='viewers-text'>
                              {' '}
                              {formatMessage({ id: 'Viewers can download your file.' })}
                            </span>
                          </label>
                        </div>
                        <div className='mb-5 form-check d-flex flex-row '>
                          <input
                            name='requireEmail'
                            type='checkbox'
                            className='form-check-input'
                            id='RequireEmailToView'
                            onChange={(event) => {
                              const isChecked = event.target.checked
                              setRequireEmail(isChecked)
                              formik.setFieldValue('requireEmail', isChecked)
                            }}
                            checked={requireEmail}
                          />
                          <label
                            className='form-check-label mx-3 mt-n1'
                            htmlFor='RequireEmailToView'
                          >
                            {formatMessage({ id: 'Require email to view' })}
                            <br></br>{' '}
                            <span className='viewers-text'>
                              {formatMessage({
                                id: 'Viewers must enter an email to view your document. ',
                              })}
                            </span>
                          </label>
                        </div>

                        <h5 className='advance-feature-heading text-dark'>
                          {formatMessage({ id: 'Advance Features ' })}
                        </h5>
                        {/* <h6 className='watermark-heading'>{formatMessage({ id: 'Watermark ' })}</h6>
                        <div className='d-flex flex-row align-items-center mb-2 mb-md-4'>
                          <div className='form-check  d-flex flex-row'>
                            <input
                              className='form-check-input'
                              type='radio'
                              name='inlineRadioOptions'
                              id='inlineRadio1'
                              value='text'
                              onChange={handleWatermarkTypeChange}
                            />
                            <label className='form-check-label mt-n1 ms-3' htmlFor='inlineRadio1'>
                              {formatMessage({ id: 'Text' })}
                            </label>
                          </div>

                          <div className='form-check  d-flex flex-row ms-7'>
                            <input
                              className='form-check-input'
                              type='radio'
                              name='inlineRadioOptions'
                              id='inlineRadio2'
                              value='image'
                              onChange={handleWatermarkTypeChange}
                            />
                            <label className='form-check-label mt-n1 ms-3' htmlFor='inlineRadio2'>
                              {formatMessage({ id: 'Image ' })}
                            </label>
                          </div>
                        </div>
                        {watermarkType === 'text' && (
                          <input
                            type='text'
                            className='form-control'
                            name='watermark'
                            id='watermarkText'
                            aria-describedby=''
                            placeholder='Enter Your Text'
                            onChange={(event) => {
                              formik.handleChange(event);
                              const watermarkText = event.target.value;
                              formik.setFieldValue('watermark', watermarkText);
                            }}
                            onBlur={formik.handleBlur}
                            value={formik.values.watermark}
                          />
                        )}
                        {watermarkType === 'image' && (
                          <div className='space-profile-image-wrapper'>
                            <p className='text-white mb-0'>{formatMessage({ id: 'Add Image' })}</p>
                            <div className='space-profile-image-preview bg-white'>
                              <DisplayImage imgName={imgName} width='130' height='130' fit='contain' alt='profile' />
                              <div onClick={handleOpen}>
                                <button
                                  type='button'
                                  className='btn btn-sm bg-white create-link-drawer-edit-button '
                                >
                                  <img src={editspaceBG} alt='edit-profile-pencil' />
                                </button>
                              </div>
                            </div>

                            <FileUpload
                              fileSize={2097152}
                              maxFileNumber={1}
                              allowType={['image/*', '.jpg', '.jpeg', '.png']}
                              metaData={{ module: 'logo', isProtected: true }}
                              modalStatus={modelStatus}
                              handleClose={handleClose}
                              handleSuccess={(id: number, name: string) => {
                                setImgName(name)
                                formik.setFieldValue('watermarkId', id)
                              }}
                            />
                          </div>
                        )} */}
                        <div className=' form-check d-flex flex-row mb-6 mt-7 '>
                          <input
                            type='checkbox'
                            className='form-check-input'
                            id='AccessControl'
                            checked={accessControlEnabled}
                            onChange={handleAccessControlChange}
                          />
                          <label className='form-check-label mx-3 mt-n1' htmlFor='AccessControl'>
                            {formatMessage({ id: 'Access Control* ' })}
                          </label>
                        </div>
                        {accessControlEnabled && aclList.length === 0 && (
                          <div className='text-danger mt-0'>
                            {formatMessage({ id: 'At least 1 email is required.' })}
                          </div>
                        )}
                        <div className='ps-md-7'>
                          <div className='d-flex flex-row align-items-center justify-content-between'>
                            <div className=' form-check d-flex flex-row  '>
                              <input
                                type='checkbox'
                                className='form-check-input'
                                name='aclEnabled'
                                id='RestrictAccess'
                                disabled={!accessControlEnabled}
                                checked={restrictAccessEnabled}
                                onChange={(e) => handleRestrictAccessChange(e, formik)}
                              />
                              <label
                                className='form-check-label mx-3 mt-n1'
                                htmlFor='RestrictAccess'
                              >
                                {formatMessage({ id: 'Restrict access*' })} <br></br>{' '}
                              </label>
                            </div>
                            <button
                              type='button'
                              className='btn btn-light edit-permissions-btn text-dark'
                              disabled={!editPermissionsEnabled || !accessControlEnabled}
                              onClick={handleEditPermissionsClick}
                            >
                              {formatMessage({ id: 'Edit Permissions' })}
                            </button>
                            <RestrictAccessDrawer
                              formik={formik}
                              aclList={aclList}
                              setAclList={setAclList}
                            />
                          </div>
                          <div className=' form-check d-flex flex-row  mt-4'>
                            <input
                              type='checkbox'
                              className='form-check-input'
                              id='EmailAuthentication'
                              name='emailAuthentication'
                              checked={emailAuthentication}
                              onChange={(e) => handleEmailAuthentication(e, formik)}
                              disabled
                            />
                            <label
                              className='form-check-label mx-3 mt-n1'
                              htmlFor='EmailAuthentication'
                            >
                              {formatMessage({ id: 'Email authentication' })} <br></br>{' '}
                              <span className='viewers-text'>
                                {formatMessage({
                                  id: 'Viewers must verify their identity via a secure link sent to their email prior to accessing your Space. ',
                                })}
                              </span>
                            </label>
                          </div>
                        </div>

                        <div
                          className='create_link-drawer-footer d-flex flex-md-row flex-column w-100 justify-content-end'
                          id='kt_footer'
                        >
                          <button
                            type='button'
                            onClick={() => handleDrawerClose(formik)}
                            className='btn btn-light flex-grow-1 flex-md-grow-0 order-2 order-md-1'
                          >
                            {formatMessage({ id: 'Cancel' })}
                          </button>
                          <div className='order-1 order-md-2'>
                            <CustomButton
                              isSubmitting={formik.isSubmitting}
                              isValid={formik.isValid}
                              buttonText={formatMessage({ id: 'Create Link' })}
                              loading={loading}
                              widthLoading={2}
                              width={2}
                              isdisable={aclList.length === 0 && accessControlEnabled}
                              buttonColor='text-white bg-primary'
                              marginButtom='mb-0'
                            />
                          </div>
                        </div>
                      </Form>
                    </div>
                  </div>
                  {/* begin::Footer */}

                  {/* end::Footer */}
                </div>
              </div>
            </div>
          )
        }}
      </Formik>
      <LinkCreatedModal
        linkId={linkId}
        onClose={onClose}
        dataRoomId={dataRoomId}
        contentId={contentId}
      />
    </>
  )
}
